// apps/patient/src/components/video/VideoJoinButton.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Loader, Clock, AlertCircle } from 'lucide-react';
import { patientApiService } from '../../services/api';

export function VideoJoinButton({ appointment, className = '' }: any) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [videoSession, setVideoSession] = useState<any>(null);
  const [checking, setChecking] = useState(true);
  
  useEffect(() => {
    checkVideoSession();
  }, [appointment.id]);
  
  const checkVideoSession = async () => {
    try {
      setChecking(true);
      const sessions = await patientApiService.getVideoSessionByAppointment(appointment.id);
      if (sessions && sessions.length > 0) {
        setVideoSession(Array.isArray(sessions) ? sessions[0] : sessions);
      }
    } catch (err) {
      console.error('Error checking video session:', err);
    } finally {
      setChecking(false);
    }
  };
  
  const handleJoinVideo = async () => {
    setLoading(true);
    try {
      navigate(`/appointments/${appointment.id}/video`);
    } catch (err) {
      console.error('Error joining video:', err);
      alert('Unable to join video visit. Please try again.');
      setLoading(false);
    }
  };
  
  // Check if appointment is eligible for video
  const isEligible = () => {
    if (!appointment.is_telehealth) return false;
    if (appointment.status === 'cancelled' || appointment.status === 'completed') return false;
    
    // Check if appointment is within valid time window (e.g., 15 minutes before to 2 hours after)
    const now = Date.now();
    const appointmentTime = appointment.start_timestamp;
    const fifteenMinutesBefore = appointmentTime - (15 * 60 * 1000);
    const twoHoursAfter = appointmentTime + (2 * 60 * 60 * 1000);
    
    return now >= fifteenMinutesBefore && now <= twoHoursAfter;
  };
  
  // Check if it's too early
  const isTooEarly = () => {
    const now = Date.now();
    const appointmentTime = appointment.start_timestamp;
    const fifteenMinutesBefore = appointmentTime - (15 * 60 * 1000);
    return now < fifteenMinutesBefore;
  };
  
  // Format time until available
  const getTimeUntilAvailable = () => {
    const appointmentTime = appointment.start_timestamp;
    const fifteenMinutesBefore = appointmentTime - (15 * 60 * 1000);
    const timeUntil = fifteenMinutesBefore - Date.now();
    const minutesUntil = Math.ceil(timeUntil / 60000);
    
    if (minutesUntil > 60) {
      const hoursUntil = Math.floor(minutesUntil / 60);
      const remainingMinutes = minutesUntil % 60;
      return `${hoursUntil}h ${remainingMinutes}m`;
    }
    
    return `${minutesUntil} min`;
  };
  
  if (checking) {
    return (
      <button
        disabled
        className={`px-4 py-3 bg-gray-200 text-gray-500 rounded-lg flex items-center justify-center ${className}`}
      >
        <Loader className="h-5 w-5 animate-spin" />
      </button>
    );
  }
  
  if (!appointment.is_telehealth) {
    return null;
  }
  
  if (isTooEarly()) {
    return (
      <button
        disabled
        className={`px-4 py-3 bg-gray-100 text-gray-600 rounded-lg flex items-center justify-center space-x-2 ${className}`}
      >
        <Clock className="h-5 w-5" />
        <span>Available in {getTimeUntilAvailable()}</span>
      </button>
    );
  }
  
  if (!isEligible()) {
    return (
      <button
        disabled
        className={`px-4 py-3 bg-gray-100 text-gray-500 rounded-lg flex items-center justify-center space-x-2 ${className}`}
      >
        <Video className="h-5 w-5" />
        <span>Video Visit Ended</span>
      </button>
    );
  }
  
  // Check if provider has started the session
  const isProviderReady = videoSession && 
    (videoSession.status === 'active' || videoSession.provider_joined_at);
  
  return (
    <button
      onClick={handleJoinVideo}
      disabled={loading}
      className={`
        px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors
        ${isProviderReady 
          ? 'bg-green-600 hover:bg-green-700 text-white animate-pulse' 
          : 'bg-primary hover:bg-primary/90 text-white'}
        ${loading ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {loading ? (
        <>
          <Loader className="h-5 w-5 animate-spin" />
          <span>Connecting...</span>
        </>
      ) : isProviderReady ? (
        <>
          <Video className="h-5 w-5" />
          <span className="font-semibold">Provider is Ready - Join Now</span>
        </>
      ) : (
        <>
          <Video className="h-5 w-5" />
          <span>Join Video Visit</span>
        </>
      )}
    </button>
  );
}